import { Box, Card, CardContent, Chip, Divider, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { TrendingUp, TrendingDown, TrendingFlat, AccountBalance, ArrowForward } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useState, useMemo } from "react";
import { useGetTransactionsQuery } from "../../services/tansactions.service";
import { useGetInvoicessQuery } from "../../services/invoices.service";
import { useAppTheme } from "../../context/ThemeContext";

function Sparkline({ values, color }: { values: number[]; color: string }) {
  if (values.length < 2) return null;
  const max = Math.max(...values, 1);
  const w = 120;
  const h = 36;
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - Math.max((v / max) * (h - 4), 0);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  const fillPts = `0,${h} ${pts} ${w},${h}`;

  return (
    <svg width={w} height={h} style={{ overflow: "visible", display: "block" }}>
      <defs>
        <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.2} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <polygon points={fillPts} fill="url(#sparkGrad)" />
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RevenueOverviewWidget() {
  const { data: transactions } = useGetTransactionsQuery("Transactions");
  const { data: invoices } = useGetInvoicessQuery("Invoices");
  const { isNewTheme, accentColor } = useAppTheme();

  // Detect available currencies from income transactions
  const currencies = useMemo(() => {
    const set = new Set<string>();
    (transactions ?? []).forEach((t) => {
      if (t.transactionType === "income" && t.currency) set.add(t.currency as string);
    });
    return Array.from(set).sort();
  }, [transactions]);

  const [currency, setCurrency] = useState<string | null>(null);
  const activeCurrency = currency ?? currencies[0] ?? null;

  const now = new Date();

  // Build last 6 months labels + income totals
  const months = useMemo(
    () =>
      Array.from({ length: 6 }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
        return { year: d.getFullYear(), month: d.getMonth() };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const monthlyIncome = months.map(({ year, month }) =>
    (transactions ?? [])
      .filter((t) => {
        if (t.transactionType !== "income") return false;
        if (activeCurrency && t.currency !== activeCurrency) return false;
        const d = new Date(t.transactionDate as any);
        return d.getFullYear() === year && d.getMonth() === month;
      })
      .reduce((sum, t) => sum + Number(t.amount), 0)
  );

  const thisMonth = monthlyIncome[5];
  const lastMonth = monthlyIncome[4];
  const pctChange = lastMonth > 0 ? ((thisMonth - lastMonth) / lastMonth) * 100 : null;

  // Outstanding: invoices where paidAmount < amount
  const outstanding = (invoices ?? [])
    .filter((inv) => {
      if (activeCurrency && inv.currency !== activeCurrency) return false;
      return Number(inv.amount) > Number(inv.paidAmount ?? 0);
    })
    .reduce((sum, inv) => sum + (Number(inv.amount) - Number(inv.paidAmount ?? 0)), 0);

  const fmt = (n: number) =>
    new Intl.NumberFormat("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  const TrendIcon =
    pctChange === null || pctChange === 0
      ? TrendingFlat
      : pctChange > 0
      ? TrendingUp
      : TrendingDown;
  const trendColor = pctChange === null || pctChange === 0 ? "#64748B" : pctChange > 0 ? "#059669" : "#EF4444";

  const cardSx = isNewTheme
    ? {
        width: "100%",
        borderRadius: "8px",
        border: "1px solid #E2E8F0",
        borderTop: `3px solid ${accentColor}`,
        boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
        transition: "box-shadow 0.15s ease",
        "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.07)" },
      }
    : {
        width: "100%",
        borderRadius: "10px",
        boxShadow: "0px 0px 55px 5px rgba(0, 0, 0, 0.05)",
      };

  return (
    <Card sx={cardSx}>
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isNewTheme && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: `${accentColor}1a`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccountBalance sx={{ fontSize: 18, color: accentColor }} />
            </Box>
          )}
          <Box>
            <Typography variant="h6" fontWeight={isNewTheme ? 700 : 600} color={isNewTheme ? "text.primary" : "black"}>
              Revenue
            </Typography>
            {isNewTheme && (
              <Box sx={{ width: 32, height: 3, borderRadius: 999, bgcolor: accentColor, mt: 0.5 }} />
            )}
          </Box>
        </Box>

        <Stack direction="row" alignItems="center" spacing={1}>
          {currencies.length > 1 && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={activeCurrency}
              onChange={(_, v) => v && setCurrency(v)}
              sx={{ "& .MuiToggleButton-root": { py: 0.25, px: 0.75, fontSize: "0.65rem", textTransform: "none", fontWeight: 600 } }}
            >
              {currencies.map((c) => (
                <ToggleButton key={c} value={c}>{c}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          <Link to="/transactions" style={{ textDecoration: "none" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, color: "primary.main", fontSize: "0.75rem", fontWeight: 600, opacity: 0.85, "&:hover": { opacity: 1 } }}>
              View all <ArrowForward sx={{ fontSize: 13 }} />
            </Box>
          </Link>
        </Stack>
      </Box>

      <Divider sx={isNewTheme ? { borderColor: "#E2E8F0" } : {}} />

      <CardContent sx={{ px: 2.5, py: 2 }}>
        <Stack direction="row" alignItems="flex-end" justifyContent="space-between">
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              This month{activeCurrency ? ` · ${activeCurrency}` : ""}
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ color: "#0F172A", lineHeight: 1.2, mt: 0.5 }}>
              {fmt(thisMonth)}
            </Typography>
            {pctChange !== null && (
              <Stack direction="row" alignItems="center" spacing={0.3} mt={0.5}>
                <TrendIcon sx={{ fontSize: 14, color: trendColor }} />
                <Typography variant="caption" sx={{ color: trendColor, fontWeight: 600 }}>
                  {pctChange > 0 ? "+" : ""}{pctChange.toFixed(1)}% vs last month
                </Typography>
              </Stack>
            )}
          </Box>

          <Box sx={{ pb: 0.5 }}>
            <Sparkline values={monthlyIncome} color={accentColor} />
            <Stack direction="row" justifyContent="space-between" mt={0.25}>
              {months.map(({ year, month }, i) => (
                <Typography key={i} variant="caption" sx={{ fontSize: "0.55rem", color: "#CBD5E1", width: `${100 / months.length}%`, textAlign: "center" }}>
                  {new Date(year, month).toLocaleString("en-US", { month: "short" })}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ my: 1.5, borderColor: "#F1F5F9" }} />

        {/* Outstanding */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              Outstanding (unpaid)
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: outstanding > 0 ? "#EF4444" : "#059669", lineHeight: 1.3 }}
            >
              {fmt(outstanding)}
            </Typography>
          </Box>
          {outstanding > 0 ? (
            <Chip
              label={`${(invoices ?? []).filter((inv) => Number(inv.amount) > Number(inv.paidAmount ?? 0) && (!activeCurrency || inv.currency === activeCurrency)).length} invoices`}
              size="small"
              color="error"
              sx={{ fontWeight: 700, borderRadius: "4px" }}
            />
          ) : (
            <Chip label="All paid" size="small" color="success" sx={{ fontWeight: 700, borderRadius: "4px" }} />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default RevenueOverviewWidget;
